"use client";

import { useTranslations } from "next-intl";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  cn,
} from "@/ui";
import { useConsent } from "../store";

interface CookieBannerProps {
  onCustomize?: () => void;
  className?: string;
  open?: boolean;
}

/**
 * Cookie consent banner displayed on first visit.
 * Allows users to accept all, reject non-essential, or customize preferences.
 */
export function CookieBanner({ onCustomize, className, open = true }: CookieBannerProps) {
  const t = useTranslations("cookie_consent");
  const { consent, isLoaded, acceptAll, rejectNonEssential } = useConsent();

  if (!(isLoaded && open) || consent.hasConsented) {
    return null;
  }

  return (
    <AlertDialog open>
      <AlertDialogContent className={cn("max-w-lg", className)}>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("title")}</AlertDialogTitle>
          <AlertDialogDescription>{t("description")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={onCustomize}
            className="text-sm"
          >
            {t("customize")}
          </Button>
          <AlertDialogAction
            variant="outline"
            size="sm"
            onClick={rejectNonEssential}
            className="text-sm"
          >
            {t("reject")}
          </AlertDialogAction>
          <AlertDialogAction
            size="sm"
            onClick={acceptAll}
            className="text-sm"
          >
            {t("accept_all")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
